import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAssetContext } from './AssetContext';
import { StateSetter } from '../types';

import gsap from 'gsap';
import { AudioContext as _AudioContext} from 'three';

type AudioData = {
  globalMuted: boolean,
  muteGlobal: StateSetter<boolean>,
  bgmMuted: boolean,
  muteBGM: StateSetter<boolean>,
  playAudio: (v: string) => void,
  enableAudio: () => void
};

type Props = JSX.ElementChildrenAttribute & {

};

export const AudioContext = createContext<AudioData>({
  bgmMuted: false,
  globalMuted: false,
  muteBGM: () => ({}),
  muteGlobal: () => ({}),
  playAudio: () => ({}),
  enableAudio: () => ({})
});

function useMutableGainNode(node: GainNode | null, muted: boolean)
{
  useEffect(() =>
  {
    if (node) {
      gsap.killTweensOf(node.gain);
      gsap.to(node.gain, { value: muted ? 0 : 1, duration: 1.0 });
    }
  }, [ node, muted ]);
}

export default function AudioContextProvider({ children }: Props): JSX.Element
{
  const audioContext = useMemo(() => _AudioContext.getContext(), []);
  const assetLibrary = useAssetContext();

  useEffect(() =>
  {
    return () =>
    {
      // make sure to destroy the old context on soft refresh
      audioContext.close();
    };
  }, [ audioContext ]);

  const mainGainNode = useMemo(() =>
  {
    const gain = audioContext.createGain();
    gain.connect(audioContext.destination);
    gain.gain.value = 1;
    return gain;
  }, [ audioContext ]);

  const bgmGainNode = useMemo(() =>
  {
    if (!mainGainNode) return null;
    const gain = audioContext.createGain();
    gain.connect(mainGainNode);
    return gain;
  }, [ audioContext, mainGainNode ]);


  const playAudio = useCallback((id: string) =>
  {
    if (!mainGainNode) return;

    const buffer = assetLibrary.get(id) as AudioBuffer;
    if (!buffer) return;

    const node = audioContext.createBufferSource();
    node.connect(mainGainNode);
    node.buffer = buffer;
    node.start(0);
  }, [ assetLibrary, audioContext, mainGainNode ]);

  const [ globalMuted, muteGlobal ] = useState(false);
  const [ bgmMuted, muteBGM ] = useState(false);

  const [isAudioInitialized, setIsAudioInitialized] = useState(false);

  const enableAudio = useCallback(() =>
  {
    if(isAudioInitialized)
      return;
    setIsAudioInitialized(true);
    audioContext.resume();

    const element = document.getElementById('bgm') as HTMLAudioElement;
    const node = audioContext.createMediaElementSource(element);
    // node.buffer = assetLibrary.get(bgmAssetID) as AudioBuffer;
    // node.loop = true;
    if (bgmGainNode)
      node.connect(bgmGainNode);

    element.play();
  }, [ audioContext, bgmGainNode, isAudioInitialized ]);

  useMutableGainNode(bgmGainNode, bgmMuted);
  useMutableGainNode(mainGainNode, globalMuted);

  return (
    <AudioContext.Provider
      value={{
        globalMuted, muteGlobal,
        bgmMuted, muteBGM,
        playAudio, enableAudio
      }}
    >
      {children}
    </AudioContext.Provider>
  );
}

export const useAudioContext: () => AudioData = () => useContext(AudioContext);